import { paramsToQuery } from '@/base/MaUtils'
import { cacheRequest, makeRequest } from '@/base/MaRequestPooler'
import Device from '@/base/constants/Device'

/**
 * Returns market intel estimation.
 * @param {string} trackId
 * @param {Array} countryCode
 * @param {string} startDate
 * @param {string} endDate
 * @param {string} page
 * @param {string} length
 * @param {string} orderBy firtSeenDate or impression
 * @returns {Promise<any>}
 */
export function getAdIntelCreatives({
  trackId,
  countryCode,
  startDate,
  endDate,
  page,
  length,
  orderBy,
}) {
  const query = paramsToQuery({
    countryCode,
    startDate,
    endDate,
    page,
    length,
    orderBy,
  })

  return cacheRequest(`/ad-intelligence/creatives/app/${trackId}?${query}`)
}

/**
 * Returns brand keywords.
 * @param trackId
 * @param trackIds
 * @param countryCode
 * @param startDate
 * @param endDate
 * @param device
 * @returns {Promise<any>}
 */
export function getBrandProtection({
  trackId,
  trackIds,
  countryCode,
  startDate,
  endDate,
  device = Device.Iphone,
}) {
  const query = paramsToQuery({
    startDate,
    endDate,
    device,
  })
  return makeRequest(
    `/app/${trackId}/${countryCode}/brand-protection?${query}`,
    {
      method: 'POST',
      data: trackIds,
    },
  )
}

/**
 * Returns brand keywords.
 * @param brandApp
 * @param keywordCount
 * @param countryCode
 * @param trackIds
 * @param store
 * @returns {Promise<any>}
 */
export function getPaidBrandProtection({
  brandApp,
  keywordCount,
  countryCode,
  trackIds,
  store,
}) {
  const query = paramsToQuery({
    brandApp,
    keywordCount,
  })
  return makeRequest(
    `/sa/stats/brand-keyword/impressions/store/${store}/country/${countryCode}?${query}`,
    {
      method: 'POST',
      data: trackIds,
    },
  )
}

/**
 * Returns apps details.
 * @param trackIds
 * @param store
 * @param countryCode
 * @returns {Promise<any>}
 */
export function getAppsDetails({
  trackIds,
  store,
  countryCode,
}) {
  const query = paramsToQuery({
    country: countryCode,
  })

  return makeRequest(`/app/${store}/info/detailed?${query}`, {
    method: 'POST',
    data: trackIds,
  })
}

/**
 * Returns apps review & rating data.
 * @param trackIds
 * @param store
 * @param startDate
 * @param endDate
 * @param country
 * @returns {Promise<any>}
 */
export function getAppsReviewRating({
  trackIds,
  store,
  startDate,
  endDate,
  country,
}) {
  const query = paramsToQuery({
    startDate,
    endDate,
    country,
  })

  return makeRequest(`/app/${store}/rating-overview?${query}`, {
    method: 'POST',
    data: trackIds,
  })
}

/**
 * Returns category rankings (with historical) data.
 * @param trackIds
 * @param store
 * @param country
 * @param device
 * @param rankings
 * @param startDate
 * @param endDate
 * @returns {Promise<any>}
 */
export function getCategoryRanks({
  trackIds,
  store,
  country,
  device,
  rankings,
  startDate,
  endDate,
}) {
  const query = paramsToQuery({
    country,
    device,
    rankings,
    startDate,
    endDate,
  })

  return makeRequest(`/categoryrankings/${store}/category-rankings-overview?${query}`, {
    method: 'POST',
    data: trackIds,
  })
}

/**
 * Returns most used colors by categoryId.
 * @param store
 * @param device
 * @param categoryId
 * @param countryCode
 * @param rankingType
 * @returns {Promise<any>}
 */
export function getMostUsedColorsByCategoryId({
  store,
  device,
  categoryId,
  countryCode,
  rankingType,
}) {
  const query = paramsToQuery({ device })
  return cacheRequest(`/categoryrankings/${store}/category-colors/${categoryId}/${countryCode}/${rankingType}?${query}`)
}

/**
 * Returns app store connect keywords.
 * @param trackIds
 * @param countryCode
 * @returns {Promise<any>}
 */
export function getAppStoreConnectKeywords({
  trackIds,
  countryCode,
}) {
  const query = paramsToQuery({
    countryCode,
  })

  return makeRequest(`/account/store-account/apps/keyword-spot?${query}`, {
    method: 'POST',
    data: trackIds,
  })
}

/**
 * Returns description keywords with count and density fields.
 * @param trackIdMap
 * @param countryCode
 * @returns {Promise<any>}
 */
export function getDescriptionsFrequency({
  trackIdMap,
  countryCode,
}) {
  return makeRequest(`/description-frequency/multiple-description/calculate`, {
    method: 'POST',
    data: {
      landCode: countryCode,
      trackIdAndDescription: trackIdMap,
    },
  })
}

/**
 * Returns app update timeline data.
 * @param trackIds
 * @param store
 * @param country
 * @param startDate
 * @param endDate
 * @returns {Promise<any>}
 */
export function getAppUpdateTimeline({
  trackIds,
  store,
  country,
  startDate,
  endDate,
}) {
  const query = paramsToQuery({
    country,
    startDate,
    endDate,
  })

  return makeRequest(`/app/${store}/version-history/detailed?${query}`, {
    method: 'POST',
    data: trackIds,
  })
}

/**
 * Returns visibility score data.
 * @param trackIds
 * @param store
 * @param countries
 * @param startDate
 * @param endDate
 * @returns {Promise<any>}
 */
export function getVisibilityScoreHistory({
  trackIds,
  store,
  countries,
  startDate,
  endDate,
}) {
  const query = paramsToQuery({
    countries,
    startDate,
    endDate,
  })

  return makeRequest(`/app/${store}/visibility-score/history?${query}`, {
    method: 'POST',
    data: trackIds,
  })
}

/**
 * Returns app cpp overview data.
 * @param trackId
 * @param countryCode
 * @param startDate
 * @param endDate
 * @returns {Promise<any>}
 */
export function getAppCPPOverview({
  trackId,
  countryCode,
  startDate,
  endDate,
}) {
  const query = paramsToQuery({
    startDate,
    endDate,
    country: countryCode,
  })

  return makeRequest(`/sa/stats/cpp/app-cpps-overview/${trackId}?${query}`)
}

/**
 * Returns ad intel advertiser summary data.
 * The trackIds limit is 5.
 * @param trackIds
 * @param platformId
 * @returns {Promise<any>}
 */
export function getAdIntelAdvertiserSummary({
  trackIds,
  platformId,
}) {
  return makeRequest(`/ad-intelligence/advertiser-summary`, {
    method: 'POST',
    data: { trackIds, platformId },
  })
}

/**
 * Returns total numbers of featuring.
 * @param trackIds
 * @param store
 * @param countryCode
 * @param startDate
 * @param endDate
 * @returns {Promise<any>}
 */
export function getTotalFeaturing({
  trackIds,
  store,
  countryCode,
  startDate,
  endDate,
}) {
  const query = paramsToQuery({
    country: countryCode,
    startDate,
    endDate,
  })

  return makeRequest(`/categoryrankings/featured-apps/${store}/featured-analysis/total?${query}`, {
    method: 'POST',
    data: trackIds,
  })
}

/**
 * Returns featuring growth.
 * @param trackIds
 * @param store
 * @param countryCode
 * @param startDate
 * @param endDate
 * @returns {Promise<any>}
 */
export function getFeaturingGrowth({
  trackIds,
  store,
  countryCode,
  startDate,
  endDate,
}) {
  const query = paramsToQuery({
    country: countryCode,
    startDate,
    endDate,
  })

  return makeRequest(`/categoryrankings/featured-apps/${store}/featured-analysis/growth?${query}`, {
    method: 'POST',
    data: trackIds,
  })
}
